import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api.js'
import { dictationSupported, createDictation } from '../lib/dictation.js'
import { tapeAvailable, startTape, blobToBase64 } from '../lib/voiceTape.js'
import { getAgentContext, subscribeAgentContext } from '../lib/agentContext.js'
import { renderMarkdown } from '../lib/markdown.js'
import { prepareImageForUpload, imageUploadName, IMAGE_EXTS, DOC_EXTS, MAX_UPLOAD_BYTES } from '../lib/chatUpload.js'
import { startDragGuard, dragEnded } from '../lib/dragGuard.js'
import ModuleCards from './ModuleCards.jsx'

const DOC_URL_REGEX = /\/api\/documents\/generated\/([^\s)"<]+)/g
const CHAT_FILE_REGEX = /\/api\/agent\/chat\/files\/([^\s)"<]+)/g

function extractDocUrls(content) {
  if (!content) return []
  const matches = []
  let match
  for (const regex of [DOC_URL_REGEX, CHAT_FILE_REGEX]) {
    const r = new RegExp(regex.source, 'g')
    while ((match = r.exec(content)) !== null) {
      const filename = decodeURIComponent(match[1]).replace(/^\d+_/, '')
      matches.push({ url: match[0], filename })
    }
  }
  return matches
}

// Rendert einen Download-Link NUR, wenn die Datei serverseitig wirklich existiert.
// Schützt vor toten Links, wenn der Agent eine download_url erfindet ohne die
// Datei tatsächlich erzeugt zu haben (Halluzination) → sonst 404 beim Klick.
function DocDownloadLink({ doc }) {
  const [status, setStatus] = useState('checking') // checking | ok | missing
  const href = `${doc.url}${doc.url.includes('?') ? '&' : '?'}token=${localStorage.getItem('agent_token')}`

  useEffect(() => {
    let alive = true
    fetch(href, { method: 'HEAD' })
      .then(res => { if (alive) setStatus(res.ok ? 'ok' : 'missing') })
      .catch(() => { if (alive) setStatus('missing') })
    return () => { alive = false }
  }, [href])

  if (status === 'checking') return null
  if (status === 'missing') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        background: 'rgba(0,0,0,0.04)', borderRadius: 8, fontSize: 12, opacity: 0.7
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span style={{ flex: 1 }}>{doc.filename} — nicht verfügbar (Datei wurde nicht erstellt)</span>
      </div>
    )
  }
  return (
    <a
      href={href}
      download
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        background: 'rgba(0,0,0,0.06)', borderRadius: 8, textDecoration: 'none',
        color: 'inherit', fontSize: 12
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.filename}</span>
    </a>
  )
}

export default function AgentWidget({ pendingActions, currentPage, onNavigate, onRefreshStats }) {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [hasNewReply, setHasNewReply] = useState(false)
  const [datenschutzAktiv, setDatenschutzAktiv] = useState(false)
  const [autoMemory, setAutoMemory] = useState(false)
  const [remembering, setRemembering] = useState(false)
  const [rememberedOk, setRememberedOk] = useState(false)
  const [panelSize, setPanelSize] = useState(() => {
    const saved = localStorage.getItem('agent-panel-size')
    return saved ? JSON.parse(saved) : { width: 420, height: 600 }
  })
  const [panelPosition, setPanelPosition] = useState(() => {
    const saved = localStorage.getItem('agent-panel-position')
    return saved ? JSON.parse(saved) : null // null = default bottom-right
  })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, corner: null })

  const [inputHeight, setInputHeight] = useState(() => {
    const saved = localStorage.getItem('agent-input-height')
    return saved ? Number(saved) : 80
  })
  const [isInputResizing, setIsInputResizing] = useState(false)
  const inputResizeStart = useRef({ y: 0, height: 0 })

  const [recording, setRecording] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const streamRef = useRef(null)
  const dictationRef = useRef(null)
  const tapeRef = useRef(null)      // parallele Tonaufnahme fuer die Whisper-Nachkorrektur
  const voiceRunRef = useRef(0)     // fortlaufende Nummer je Diktatlauf — alte Korrekturen verfallen
  const dictPrefixRef = useRef('')  // Text, der beim Diktatstart schon im Feld stand
  const [polishing, setPolishing] = useState(false)
  const [pendingSendUi, setPendingSendUi] = useState(false) // "wird gleich gesendet"-Hinweis
  const polishingRef = useRef(false) // synchron lesbar (State waere im Send-Closure evtl. alt)
  const pendingSendRef = useRef(null) // waehrend der Nachkorrektur abgeschicktes Senden — wird nachgeholt
  const sendNowRef = useRef(null)   // immer die frischeste sendMessage-Fassung (Closure-Falle)
  // Für das nachgeholte Senden aus polishTape: dessen Closure ist vom Render
  // des Diktatstarts — dieser Ref zeigt immer auf die frischeste Fassung.
  useEffect(() => { sendNowRef.current = sendMessage })

  // Beim Unmount laufendes Diktat + Tonaufnahme stoppen (Mikro freigeben).
  useEffect(() => () => {
    dictationRef.current?.stop?.(); dictationRef.current = null
    tapeRef.current?.cancel?.(); tapeRef.current = null
  }, [])

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const panelRef = useRef(null)
  const fileInputRef = useRef(null)
  const [fileDragging, setFileDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [pendingFiles, setPendingFiles] = useState([]) // angehängte, noch nicht gesendete Dateien

  // Listen for sidebar toggle event
  useEffect(() => {
    api.getUserSettings().then(s => {
      setDatenschutzAktiv(s.datenschutz_modus === 'true')
      setAutoMemory(s.auto_memory === 'true')
    }).catch(() => {})
  }, [open])

  useEffect(() => {
    function handleToggle() {
      setOpen(prev => !prev)
      setHasNewReply(false)
    }
    window.addEventListener('toggle-agent-widget', handleToggle)
    return () => window.removeEventListener('toggle-agent-widget', handleToggle)
  }, [])

  // Save panel size to localStorage
  useEffect(() => {
    localStorage.setItem('agent-panel-size', JSON.stringify(panelSize))
  }, [panelSize])

  // Save panel position to localStorage
  useEffect(() => {
    if (panelPosition) {
      localStorage.setItem('agent-panel-position', JSON.stringify(panelPosition))
    }
  }, [panelPosition])

  // Prevent text selection during drag/resize (incl. Safari -webkit prefix)
  useEffect(() => {
    if (isDragging || isResizing || isInputResizing) {
      document.body.style.userSelect = 'none'
      document.body.style.webkitUserSelect = 'none'
      document.body.style.cursor = isDragging ? 'grabbing' : isInputResizing ? 'ns-resize' : 'inherit'
    } else {
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
      document.body.style.cursor = ''
    }
    return () => {
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
      document.body.style.cursor = ''
    }
  }, [isDragging, isResizing, isInputResizing])

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      e.preventDefault()
      if (dragEnded(e)) { setIsDragging(false); return }
      const deltaX = e.clientX - dragStart.x
      const deltaY = e.clientY - dragStart.y

      setPanelPosition(prev => {
        const current = prev || { x: window.innerWidth - panelSize.width - 20, y: window.innerHeight - panelSize.height - 20 }
        return {
          x: Math.max(0, Math.min(window.innerWidth - panelSize.width, current.x + deltaX)),
          y: Math.max(0, Math.min(window.innerHeight - panelSize.height, current.y + deltaY))
        }
      })
      setDragStart({ x: e.clientX, y: e.clientY })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    const endGuard = startDragGuard()
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      endGuard()
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart, panelSize])

  // Handle resizing
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      e.preventDefault()
      if (dragEnded(e)) { setIsResizing(false); return }
      const deltaX = e.clientX - resizeStart.x
      const deltaY = e.clientY - resizeStart.y

      setPanelSize(prev => {
        let newWidth = prev.width
        let newHeight = prev.height
        let newX = panelPosition?.x
        let newY = panelPosition?.y

        // Calculate new dimensions based on corner
        if (resizeStart.corner.includes('e')) {
          newWidth = Math.max(320, Math.min(800, resizeStart.width + deltaX))
        }
        if (resizeStart.corner.includes('w')) {
          newWidth = Math.max(320, Math.min(800, resizeStart.width - deltaX))
          if (panelPosition) newX = resizeStart.posX + deltaX
        }
        if (resizeStart.corner.includes('s')) {
          newHeight = Math.max(400, Math.min(window.innerHeight * 0.9, resizeStart.height + deltaY))
        }
        if (resizeStart.corner.includes('n')) {
          newHeight = Math.max(400, Math.min(window.innerHeight * 0.9, resizeStart.height - deltaY))
          if (panelPosition) newY = resizeStart.posY + deltaY
        }

        // Update position if resizing from left/top
        if (newX !== panelPosition?.x || newY !== panelPosition?.y) {
          setPanelPosition({ x: newX, y: newY })
        }

        return { width: newWidth, height: newHeight }
      })
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    const endGuard = startDragGuard()
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      endGuard()
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, resizeStart, panelPosition])

  // Handle input area resizing (drag handle above textarea)
  useEffect(() => {
    if (!isInputResizing) return

    const handleMouseMove = (e) => {
      e.preventDefault()
      if (dragEnded(e)) { handleMouseUp(); return }
      const deltaY = inputResizeStart.current.y - e.clientY // inverted: drag up = bigger
      const newHeight = Math.max(60, Math.min(400, inputResizeStart.current.height + deltaY))
      setInputHeight(newHeight)
    }

    const handleMouseUp = () => {
      setIsInputResizing(false)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      localStorage.setItem('agent-input-height', String(inputHeight))
    }

    const endGuard = startDragGuard()
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'ns-resize'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      endGuard()
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isInputResizing, inputHeight])

  // Start dragging
  const handleDragStart = (e) => {
    if (e.target.closest('.agent-widget-close, .agent-widget-kbd')) return
    e.preventDefault()
    window.getSelection()?.removeAllRanges()
    setIsDragging(true)
    setDragStart({ x: e.clientX, y: e.clientY })
  }

  // Start resizing
  const handleResizeStart = (e, corner) => {
    e.preventDefault()
    e.stopPropagation()
    window.getSelection()?.removeAllRanges()
    // If panel is in default position (right/bottom anchored), convert to absolute position first
    let posX = panelPosition?.x
    let posY = panelPosition?.y
    if (!panelPosition && panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect()
      posX = rect.left
      posY = rect.top
      setPanelPosition({ x: posX, y: posY })
    }
    setIsResizing(true)
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: panelSize.width,
      height: panelSize.height,
      posX: posX || 0,
      posY: posY || 0,
      corner
    })
  }


  // Aktiver Chat (gesteuert vom Urs-Seiten-State, persistiert in localStorage)
  const [activeChatId, setActiveChatId] = useState(() => localStorage.getItem('urs_active_chat') || null)
  const [activeChatTitle, setActiveChatTitle] = useState(null)

  // --- Mehrere Fäden parallel: Umschalt-Leiste + Hintergrund-Status ------------
  // Alle offenen Fäden (nur Liste, KEIN LLM-Aufruf). Damit kann man einen Faden
  // weiterrechnen lassen (z.B. Bauablaufplan) und parallel in einem anderen Faden
  // arbeiten (z.B. E-Mail) — ohne dass der erste abbricht.
  const [threads, setThreads] = useState([])
  const viewedChatRef = useRef(activeChatId)
  useEffect(() => { viewedChatRef.current = activeChatId }, [activeChatId])

  const loadThreads = useCallback(() => {
    api.chatsList(false).then(list => setThreads(Array.isArray(list) ? list : [])).catch(() => {})
  }, [])

  // Beim Öffnen / Faden-Wechsel die Liste laden
  useEffect(() => { if (open) loadThreads() }, [open, activeChatId, loadThreads])

  // Solange etwas rechnet (aktiver Sendevorgang ODER ein losgelöster Hintergrund-Faden),
  // alle 5s auffrischen, damit "läuft → fertig" in der Leiste sichtbar wird.
  useEffect(() => {
    if (!open) return
    const anyRunning = sending || threads.some(t => t.job_status === 'running')
    if (!anyRunning) return
    const id = setInterval(loadThreads, 5000)
    return () => clearInterval(id)
  }, [open, sending, threads, loadThreads])

  const activeThread = threads.find(t => t.conversation_id === activeChatId) || null
  // Rechnet der gerade betrachtete Faden im Hintergrund (kein aktiver Live-Stream)?
  const activeRunning = activeThread?.job_status === 'running'

  // Losgelöster Faden gerade fertig geworden → Nachrichten neu laden (Antwort abholen)
  const wasActiveRunningRef = useRef(false)
  useEffect(() => {
    if (wasActiveRunningRef.current && !activeRunning && activeChatId && !sending) {
      api.chatMessages(activeChatId).then(({ session, messages }) => {
        setActiveChatTitle(session?.title || null)
        const parsed = (messages || []).map(m => {
          if (m.metadata_json) { try { return { ...m, metadata: JSON.parse(m.metadata_json) } } catch { /* ignore */ } }
          return m
        })
        setMessages(parsed)
      }).catch(() => {})
    }
    wasActiveRunningRef.current = activeRunning
  }, [activeRunning, activeChatId, sending])

  // "Wo bin ich gerade"-Bezug (z.B. offene E-Mail) — als wegklickbarer Chip.
  const [pageContext, setPageContext] = useState(() => getAgentContext())
  const [contextDismissed, setContextDismissed] = useState(false)
  useEffect(() => {
    return subscribeAgentContext((ctx) => {
      setPageContext(ctx)
      setContextDismissed(false) // neuer Bezug → Chip wieder anbieten
    })
  }, [])
  // Aktiver Chip: nur zeigen, wenn ein Bezug da ist und der User ihn nicht entfernt hat
  const activeContext = pageContext && !contextDismissed ? pageContext : null

  // Gibt es zu diesem Bezug schon einen Chat-Faden? → automatisch dorthin wechseln.
  // E-Mail: via Header-Kette. Andere Bezüge (Kontakt, Werkvertrag …): via
  // bezug_key — ein Bezug hat höchstens einen aktiven Faden, keine Duplikate mehr.
  // "project" nimmt das Backend bewusst aus (zu breit → ewiger Sammel-Faden).
  useEffect(() => {
    if (!pageContext || !pageContext.type || !pageContext.id) return
    let cancelled = false
    const isEmail = pageContext.type === 'email'
    api.contextThread(isEmail
      ? { emailId: pageContext.id }
      : { bezugKey: `${pageContext.type}:${pageContext.id}` })
      .then(({ thread }) => {
        if (cancelled) return
        if (!thread?.conversation_id) return
        // Während eines laufenden Sendevorgangs den Faden nicht unter der Antwort wegziehen
        if (abortRef.current) return
        const current = localStorage.getItem('urs_active_chat') || null
        if (thread.conversation_id === current) return
        localStorage.setItem('urs_active_chat', thread.conversation_id)
        setActiveChatId(thread.conversation_id)
        window.dispatchEvent(new CustomEvent('urs-active-chat-changed'))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [pageContext])

  // Wenn der User auf der Urs-Seite einen anderen Chat selektiert, soll das
  // Widget mitkriegen — Storage-Event (cross-tab) + Custom-Event (same-tab).
  useEffect(() => {
    const sync = () => setActiveChatId(localStorage.getItem('urs_active_chat') || null)
    window.addEventListener('storage', sync)
    window.addEventListener('urs-active-chat-changed', sync)
    return () => {
      window.removeEventListener('storage', sync)
      window.removeEventListener('urs-active-chat-changed', sync)
    }
  }, [])

  // Lazy load: Messages des aktiven Chats laden (oder leer wenn keiner aktiv)
  useEffect(() => {
    if (!open) return
    if (!activeChatId) {
      setMessages([])
      setLoaded(true)
      setActiveChatTitle(null)
      return
    }
    api.chatMessages(activeChatId).then(({ session, messages }) => {
      setActiveChatTitle(session?.title || null)
      const parsed = (messages || []).map(m => {
        if (m.metadata_json) {
          try { return { ...m, metadata: JSON.parse(m.metadata_json) } } catch { /* ignore */ }
        }
        return m
      })
      setMessages(parsed)
      setLoaded(true)
    }).catch(err => console.error('[AgentWidget] chatMessages:', err))
  }, [open, activeChatId])

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Cmd+K toggle & Escape close
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      audioChunksRef.current = []

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
            ? 'audio/mp4'
            : ''
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        streamRef.current = null

        const actualType = mediaRecorder.mimeType || mimeType || 'audio/mp4'
        const blob = new Blob(audioChunksRef.current, { type: actualType })
        if (blob.size < 1000) { setRecording(false); return }

        setRecording(false)
        setTranscribing(true)

        try {
          const reader = new FileReader()
          const base64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1])
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
          const result = await api.voiceToText(base64, actualType)
          if (result.text) {
            setInput(prev => prev ? prev + ' ' + result.text : result.text)
            setTimeout(() => inputRef.current?.focus(), 50)
          }
        } catch (err) {
          console.error('[AgentWidget] Transkription fehlgeschlagen:', err)
        } finally {
          setTranscribing(false)
        }
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(250)
      setRecording(true)
    } catch (err) {
      console.error('[AgentWidget] Mikrofon-Zugriff fehlgeschlagen:', err)
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }

  // Aufnahme stoppen, von Whisper nachhören lassen, saubere Fassung einsetzen
  // (Cockpit-Muster; Details s. Chat.jsx polishTape).
  async function polishTape(run) {
    const tape = tapeRef.current
    if (!tape) return
    tapeRef.current = null
    // Signal SYNCHRON setzen (vor dem ersten await): sendMessage prüft es beim
    // Klick — sonst ginge "stoppen und gleich senden" mit der rohen Fassung raus.
    polishingRef.current = true
    setPolishing(true)
    let finalText = null
    try {
      let rec = null
      try { rec = await tape.stop() } catch { /* Aufnahme kaputt → Live-Text bleibt */ }
      if (rec && rec.blob.size >= 2000) {
        const base64 = await blobToBase64(rec.blob)
        const result = await api.voiceToText(base64, rec.mimeType)
        const text = (result.text || '').trim()
        if (text && voiceRunRef.current === run) {
          finalText = dictPrefixRef.current + text
          setInput(finalText)
        }
      }
    } catch (err) {
      console.warn('[AgentWidget] Whisper-Nachkorrektur nicht möglich:', err?.message || err)
    } finally {
      polishingRef.current = false
      setPolishing(false)
      // Während der Korrektur gesendet? Jetzt nachholen — mit der sauberen
      // Fassung, wenn der gesendete Text der (rohe) Live-Text war.
      const pending = pendingSendRef.current
      if (pending) {
        pendingSendRef.current = null
        setPendingSendUi(false)
        const useCorrected = finalText && pending.raw != null && pending.text === pending.raw.trim()
        // Feld leeren: handleSubmit hatte es geleert, aber setInput(finalText)
        // oben hat es wieder gefüllt — sonst bleibt der gesendete Text stehen.
        setInput('')
        sendNowRef.current?.(useCorrected ? finalText : pending.text, pending.images, pending.attachments)
      }
    }
  }

  // Mikrofon-Knopf: bevorzugt Live-Diktat (Wörter erscheinen beim Sprechen).
  // Parallel läuft eine Tonaufnahme mit; nach dem Stopp ersetzt die
  // Whisper-Nachkorrektur den Live-Text (trifft Fachbegriffe).
  // Ohne Web-Speech-Unterstützung Fallback auf Aufnahme → lokale Transkription.
  async function startVoice() {
    if (dictationSupported()) {
      const run = ++voiceRunRef.current
      dictPrefixRef.current = input ? input.replace(/\s*$/, '') + ' ' : ''
      if (tapeAvailable()) {
        try { tapeRef.current = await startTape() } catch { tapeRef.current = null }
      }
      const handle = createDictation({
        base: input,
        onText: (text) => setInput(text),
        onError: (e) => { console.error('[AgentWidget] Diktat-Fehler:', e?.error || e); dictationRef.current = null; setRecording(false) },
        onEnd: () => { dictationRef.current = null; setRecording(false); polishTape(run); setTimeout(() => inputRef.current?.focus(), 50) }
      })
      if (handle) { dictationRef.current = handle; setRecording(true); return }
      tapeRef.current?.cancel?.()
      tapeRef.current = null
    }
    startRecording()
  }

  function stopVoice() {
    if (dictationRef.current) {
      const rec = dictationRef.current
      dictationRef.current = null
      // Nachhören ZUERST anstossen: setzt die Senden-Sperre noch synchron im
      // Klick (onEnd der Erkennung kommt erst Ticks später).
      polishTape(voiceRunRef.current)
      rec.stop()
      setRecording(false)
      return
    }
    stopRecording()
  }

  const abortRef = useRef(null)
  // conversationId des laufenden Sendevorgangs — für gezieltes Abbrechen serverseitig
  const inflightConvIdRef = useRef(null)

  function stopStreaming() {
    // Job serverseitig wirklich beenden (ein Verbindungsabbruch stoppt ihn bewusst
    // nicht mehr), dann den Stream lokal abbrechen.
    const cid = inflightConvIdRef.current
    if (cid) api.chatCancel(cid).catch(() => {})
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
  }

  // Faden wechseln OHNE Abbruch: nur den Client loslösen — der Job rechnet
  // serverseitig weiter (kein chatCancel). In der Leiste blinkt solange "läuft",
  // beim Zurückkommen erscheint das fertige Ergebnis.
  function detachActiveStream() {
    if (abortRef.current) {
      abortRef.current._detached = true
      abortRef.current.abort()
      abortRef.current = null
      setSending(false)
    }
  }
  function switchThread(cid) {
    if (cid === activeChatId) return
    detachActiveStream()
    localStorage.setItem('urs_active_chat', cid)
    setActiveChatId(cid)
    window.dispatchEvent(new CustomEvent('urs-active-chat-changed'))
  }
  function startNewThread() {
    detachActiveStream()
    localStorage.removeItem('urs_active_chat')
    setActiveChatId(null)
    setActiveChatTitle(null)
    setMessages([])
    setLoaded(true)
    window.dispatchEvent(new CustomEvent('urs-active-chat-changed'))
  }

  // Merken-Knopf: merkt diesen Faden gezielt (nur relevant wenn auto_memory AUS).
  async function rememberCurrent() {
    if (!activeChatId || remembering || autoMemory) return
    setRemembering(true)
    setRememberedOk(false)
    try {
      await api.chatRemember(activeChatId)
      setRememberedOk(true)
      setTimeout(() => setRememberedOk(false), 2500)
    } catch {
      /* still, aber nicht blockierend */
    } finally {
      setRemembering(false)
    }
  }

  function handleSubmit(e) {
    e?.preventDefault?.()
    if (sending || uploading) return
    const note = input.trim()
    if (!note && pendingFiles.length === 0) return

    const images = pendingFiles.filter(f => f.kind === 'image').map(f => ({ ...f.img, storedName: f.storedName }))
    const docs = pendingFiles.filter(f => f.kind === 'doc')
    const attachments = docs.map(f => ({ filename: f.filename, size: f.size, ext: f.ext }))
    // Versteckte Hinweise für Urs (im UI ausgeblendet)
    const docTags = docs
      .map(f => `[Hochgeladene Datei: ${f.filename} | lies sie mit read_chat_file filename="${f.storedName}"]`)
      .join('\n')

    let text
    if (note && docTags) text = `${note}\n\n${docTags}`
    else if (docTags) text = docTags
    else if (note) text = note
    else text = `[Bild angehängt: ${images.map(i => i.filename).join(', ')}]`

    setInput('')
    setPendingFiles([])
    sendMessage(text, images.length ? images : undefined, attachments.length ? attachments : undefined)
  }

  async function sendMessage(text, images, attachments) {
    const msg = (text ?? '').trim()
    if (!msg || sending) return

    // Läuft noch die Whisper-Nachkorrektur, wird das Senden ZURÜCKGEHALTEN und
    // nach der Korrektur automatisch nachgeholt (mit der sauberen Fassung).
    if (polishingRef.current) {
      pendingSendRef.current = { text: msg, images, attachments, raw: input }
      setPendingSendUi(true)
      return
    }

    // Eine danach eintrudelnde (verspätete) Korrektur darf das geleerte Feld
    // nicht wieder füllen — der Lauf verfällt.
    voiceRunRef.current++

    // Aktueller Bezug (z.B. offene E-Mail, Stundenliste, Dokument). Fliesst als
    // Grounding mit JEDER Nachricht mit, fragmentiert den Faden aber NICHT mehr:
    // wie ein menschlicher Mitarbeiter springt Urs im selben Gespräch zwischen
    // Themen — das Backend spielt bei einem Bezug-Wechsel den vollen Block ein,
    // sonst nur eine schlanke Erinnerungs-Zeile.
    const ctx = activeContext
    setSending(true)

    const controller = new AbortController()
    abortRef.current = controller
    inflightConvIdRef.current = activeChatId || null
    // Gilt dieser Stream noch für den GERADE betrachteten Faden? (Nur dann UI updaten.)
    // Beim Faden-Wechsel wird der Controller losgelöst → Updates gehen ins Leere,
    // der Server rechnet aber weiter.
    const applies = () => abortRef.current === controller && !controller._detached

    const newExchange = [
      { role: 'user', content: msg, images: images || undefined, attachments: attachments || undefined, created_at: new Date().toISOString() },
      { role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }
    ]
    // Immer an den laufenden Faden anhängen (rollender Faden)
    setMessages(prev => [...prev, ...newExchange])

    try {
      const result = await api.chatStream(msg, {
        project_id: null,
        currentPage,
        conversationId: activeChatId,
        context: ctx || undefined,
        signal: controller.signal,
        images,
        onInit: (cid) => { inflightConvIdRef.current = cid },
        onToken: (token) => {
          if (!applies()) return
          setMessages(prev => {
            const next = [...prev]
            const last = next[next.length - 1]
            if (last?.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + token }
            }
            return next
          })
        },
        onTool: (toolEvent) => {
          if (!applies()) return
          if (toolEvent.status === 'running') {
            setMessages(prev => {
              const next = [...prev]
              const last = next[next.length - 1]
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, toolName: toolEvent.name }
              }
              return next
            })
          }
        }
      })

      if (applies()) {
        // Backend hat ggf. eine neue Conversation angelegt — übernehmen +
        // andere Tabs / Urs-Page benachrichtigen
        if (result?.conversation_id && result.conversation_id !== activeChatId) {
          localStorage.setItem('urs_active_chat', result.conversation_id)
          setActiveChatId(result.conversation_id)
          window.dispatchEvent(new CustomEvent('urs-active-chat-changed'))
        }

        // Finalize: replace with complete reply from done event
        setMessages(prev => {
          const next = [...prev]
          const last = next[next.length - 1]
          if (last?.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              content: result?.reply || last.content,
              streaming: false,
              toolName: null,
              metadata: result?.metadata || null,
              provider: result?.provider,
              model: result?.model
            }
          }
          return next
        })

        if (result?.pending_actions?.length > 0 || result?.tool_calls_count > 0) {
          onRefreshStats?.()
        }
        if (!open) {
          setHasNewReply(true)
        }
      }
      loadThreads()
    } catch (err) {
      const isAbort = err?.name === 'AbortError' || controller.signal.aborted
      // Losgelöst (Faden gewechselt): Job rechnet serverseitig weiter — View nicht anfassen.
      if (controller._detached) { loadThreads(); return }
      if (!isAbort) {
        console.error('[AgentWidget] Stream-Fehler:', err)
      }
      // Verbindungsabriss (Stall, Standby, Netzwechsel): Der Job rechnet server-
      // seitig weiter bzw. die Antwort liegt schon in der DB → nachladen statt
      // Endlos-Spinner/Fehlermeldung. Bei echtem Backend-Fehler (kein cid oder
      // Reload schlägt fehl) fällt es unten auf die Fehleranzeige zurück.
      if (!isAbort && applies()) {
        const cid = inflightConvIdRef.current || activeChatId
        if (cid) {
          try {
            const { session, messages: dbMsgs } = await api.chatMessages(cid)
            const parsed = (dbMsgs || []).map(m => {
              if (m.metadata_json) { try { return { ...m, metadata: JSON.parse(m.metadata_json) } } catch { /* ignore */ } }
              return m
            })
            if (applies()) {
              if (cid !== activeChatId) {
                localStorage.setItem('urs_active_chat', cid)
                setActiveChatId(cid)
                window.dispatchEvent(new CustomEvent('urs-active-chat-changed'))
              }
              setActiveChatTitle(session?.title || null)
              if (parsed[parsed.length - 1]?.role === 'assistant') {
                // Antwort ist schon da — einfach anzeigen
                setMessages(parsed)
              } else {
                // Job läuft noch: Hinweis-Bubble; das job_status-Polling lädt
                // die fertige Antwort automatisch nach (running → done).
                setMessages([...parsed, {
                  role: 'assistant',
                  content: '*Verbindung kurz unterbrochen — ich arbeite im Hintergrund weiter. Die Antwort erscheint hier automatisch.*',
                  created_at: new Date().toISOString(),
                  streaming: false
                }])
              }
              loadThreads()
              return
            }
          } catch { /* Reload fehlgeschlagen → Fehleranzeige unten */ }
        }
      }
      if (applies()) {
        setMessages(prev => {
          const next = [...prev]
          const last = next[next.length - 1]
          if (last?.role === 'assistant') {
            const suffix = isAbort ? '\n\n*— Abgebrochen*' : ''
            const errorMsg = err?.message || 'Unbekannter Fehler'
            const displayMsg = isAbort
              ? (last.content || 'Abgebrochen.') + suffix
              : last.content
                ? last.content + `\n\n---\n⚠ Verarbeitung abgebrochen: ${errorMsg}`
                : `Fehler bei der Verarbeitung: ${errorMsg}`
            next[next.length - 1] = { ...last, content: displayMsg, streaming: false, toolName: null }
          }
          return next
        })
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        setSending(false)
      }
    }
  }

  // Mehrere Dateien nacheinander hochladen (Datei-Dialog mit multiple, Drag&Drop, Paste)
  async function handleFilesUpload(fileList) {
    if (sending || uploading) return
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        await uploadOneFile(file)
      }
    } finally {
      setUploading(false)
    }
  }
  async function uploadOneFile(file) {
    if (!file) return

    const allowedExts = [...DOC_EXTS, ...IMAGE_EXTS]
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!allowedExts.includes(ext)) {
      alert(`Dateityp .${ext} nicht unterstützt. Erlaubt: ${allowedExts.join(', ')}`)
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      alert('Datei zu gross (max. 20 MB)')
      return
    }

    const isImage = IMAGE_EXTS.includes(ext)
    try {
      if (isImage) {
        // Bilder direkt als Vision-Anhang (grosse Bilder werden auf max. 1800px verkleinert)
        const img = await prepareImageForUpload(file)
        // Zusätzlich auf die Platte legen, damit Urs im Claude-Code-Modus das Bild
        // via read_chat_file (Vision) lesen kann — base64 wird dort nicht durchgereicht.
        const up = await api.uploadChatFile(imageUploadName(img, file.name), img.data)
        setPendingFiles(prev => [...prev, { kind: 'image', img, storedName: up.storedName, filename: file.name, ext }])
      } else {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result.split(',')[1])
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
        const result = await api.uploadChatFile(file.name, base64)
        const sizeKB = (result.size / 1024).toFixed(0)
        setPendingFiles(prev => [...prev, { kind: 'doc', storedName: result.storedName, filename: file.name, size: sizeKB, ext }])
      }
    } catch (err) {
      console.error('[AgentWidget] Upload fehlgeschlagen:', err)
      alert(`Upload von "${file.name}" fehlgeschlagen: ` + err.message)
    }
  }

  function removePendingFile(idx) {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx))
  }

  function widgetHasFiles(e) {
    const types = e.dataTransfer?.types
    if (!types) return false
    return types.includes('Files') || types.includes('public.file-url') || types.includes('text/uri-list')
  }
  function handleFileDragOver(e) {
    if (!widgetHasFiles(e)) return
    e.preventDefault()
    e.stopPropagation()
    setFileDragging(true)
  }
  function handleFileDragLeave(e) {
    if (!widgetHasFiles(e)) return
    e.preventDefault()
    setFileDragging(false)
  }
  function handleFileDrop(e) {
    if (!widgetHasFiles(e)) return
    e.preventDefault()
    e.stopPropagation()
    setFileDragging(false)
    handleFilesUpload(e.dataTransfer.files)
  }

  function toggleOpen() {
    setOpen(prev => !prev)
    setHasNewReply(false)
  }

  // Hide completely on agent and chat page
  if (currentPage === 'agent' || currentPage === 'chat') return null

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          className="agent-widget-panel"
          style={{
            position: 'fixed',
            width: `${panelSize.width}px`,
            height: `${panelSize.height}px`,
            right: panelPosition ? 'auto' : '20px',
            bottom: panelPosition ? 'auto' : '80px',
            left: panelPosition ? `${panelPosition.x}px` : 'auto',
            top: panelPosition ? `${panelPosition.y}px` : 'auto',
            display: 'flex',
            flexDirection: 'column',
            userSelect: isDragging ? 'none' : 'auto'
          }}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          onDrop={handleFileDrop}
        >
          {fileDragging && (
            <div
              onDragOver={handleFileDragOver}
              onDrop={handleFileDrop}
              style={{
                position: 'absolute', inset: 0, zIndex: 30,
                background: 'rgba(0,0,0,0.55)', borderRadius: 'inherit',
                border: '2px dashed var(--color-primary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 10, color: '#fff', pointerEvents: 'auto'
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Datei hier ablegen</span>
            </div>
          )}
          {/* Resize Handles - alle 4 Ecken */}
          <div className="resize-handle resize-handle-corner resize-handle-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
          <div className="resize-handle resize-handle-corner resize-handle-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
          <div className="resize-handle resize-handle-corner resize-handle-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
          <div className="resize-handle resize-handle-corner resize-handle-se" onMouseDown={(e) => handleResizeStart(e, 'se')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M11 11L11 8M11 11L8 11M11 5L11 2M11 5L5 11M11 2L2 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Kanten fuer Resize */}
          <div className="resize-handle resize-handle-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
          <div className="resize-handle resize-handle-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
          <div className="resize-handle resize-handle-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
          <div className="resize-handle resize-handle-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />

          <div
            className="agent-widget-header"
            onMouseDown={handleDragStart}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="agent-widget-header-left">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="11.1" y="3" width="1.8" height="5.6" rx="0.9" fill="currentColor" stroke="none" />
                <rect x="3" y="12" width="18" height="9.5" rx="3" />
                <circle cx="8.6" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
                <circle cx="15.4" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
              </svg>
              <span title={activeChatTitle || 'Neuer Chat'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, display: 'inline-block' }}>
                {activeChatTitle || 'Neuer Chat'}
              </span>
              {datenschutzAktiv && (
                <span title="Datenschutzmodus aktiv — PII wird anonymisiert" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,0.15)',
                  padding: '1px 6px', borderRadius: 8, fontWeight: 500, marginLeft: 4
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Geschützt
                </span>
              )}
            </div>
            <div className="agent-widget-header-right">
              <button
                className="agent-widget-close"
                onClick={() => { setOpen(false); onNavigate('chat') }}
                title="Vollbild"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
              <kbd className="agent-widget-kbd">⌘K</kbd>
              <button className="agent-widget-close" onClick={() => setOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Faden-Leiste: zwischen Fäden wechseln, ohne laufende abzubrechen.
              Oranger Punkt = Urs rechnet in diesem Faden gerade (auch im Hintergrund).
              Links scrollende Faden-Pillen, rechts fix die Aktionen:
              "Merken" (nur wenn Automatik AUS) + "Neues Thema" (Themengrenze selbst ziehen). */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px',
            flexShrink: 0,
            background: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.10)'
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', flex: 1, minWidth: 0 }}>
            {threads.slice(0, 8).map(t => {
              const isActive = t.conversation_id === activeChatId
              const running = t.job_status === 'running'
              return (
                <button
                  key={t.conversation_id}
                  type="button"
                  onClick={() => switchThread(t.conversation_id)}
                  title={(running ? '● läuft — ' : '') + (t.title || 'Chat')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                    maxWidth: 150, padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
                    border: isActive ? '1px solid rgba(255,255,255,0.72)' : '1px solid rgba(255,255,255,0.28)',
                    background: isActive ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.82)',
                    fontSize: 12, fontWeight: isActive ? 600 : 400, lineHeight: 1.4
                  }}
                >
                  {running && (
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                      background: '#FF5500',
                      animation: 'urs-thread-pulse 1.3s ease-in-out infinite'
                    }} />
                  )}
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.title || 'Neuer Chat'}
                  </span>
                </button>
              )
            })}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
              {!autoMemory && (
                <button
                  type="button"
                  onClick={rememberCurrent}
                  disabled={!activeChatId || remembering}
                  title={activeChatId ? 'Dieses Thema dauerhaft merken' : 'Erst ein Thema öffnen'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
                    padding: '4px 11px', borderRadius: 999,
                    cursor: (!activeChatId || remembering) ? 'default' : 'pointer',
                    border: rememberedOk ? '1px solid rgba(255,85,0,0.9)' : '1px solid rgba(255,255,255,0.28)',
                    background: rememberedOk ? 'rgba(255,85,0,0.22)' : 'rgba(255,255,255,0.08)',
                    color: (!activeChatId || remembering) ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.92)',
                    fontSize: 12, fontWeight: 500, lineHeight: 1.4, whiteSpace: 'nowrap'
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  </svg>
                  {rememberedOk ? 'Gemerkt' : (remembering ? 'Merke…' : 'Merken')}
                </button>
              )}
              <button
                type="button"
                onClick={startNewThread}
                title="Neues Thema beginnen"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
                  padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.32)', background: 'rgba(255,255,255,0.10)',
                  color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 500, lineHeight: 1.4, whiteSpace: 'nowrap'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Neues Thema
              </button>
            </div>
          </div>

          {pendingActions > 0 && (
            <button className="agent-widget-actions-banner" onClick={() => { setOpen(false); onNavigate('agent') }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{pendingActions} ausstehende Aktion{pendingActions !== 1 ? 'en' : ''}</span>
              <span className="agent-widget-actions-link">Anzeigen</span>
            </button>
          )}

          <div className="agent-widget-messages">
            {loaded && messages.length === 0 && (
              <div className="agent-widget-empty">
                <p>Frag mich etwas über deine Emails, Projekte oder Kontakte.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}`}>
                {msg.role === 'assistant' ? (
                  msg.streaming && !msg.content ? (
                    msg.toolName ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
                        <div className="spinner spinner-sm" />
                        {msg.toolName.replace(/^mcp__urs__/, '')}...
                      </span>
                    ) : (
                      <div className="spinner spinner-sm" />
                    )
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  )
                ) : (<>
                  {msg.content.replace(/\n?\n?\[Pfad: [^\]]+\]/g, '').replace(/\n?\n?\[Hochgeladene Datei: [^\]]+\]/g, '').replace(/\n?\n?\[Bild angehängt: [^\]]+\]/g, '').trim()}
                  {msg.images?.map((img, j) => (
                    <img key={j} src={`data:${img.media_type};base64,${img.data}`}
                      alt={img.filename} style={{ display: 'block', maxWidth: 200, maxHeight: 160, borderRadius: 6, marginTop: 6 }} />
                  ))}
                  {msg.attachments?.map((att, j) => (
                    <div key={j} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 6,
                      padding: '6px 10px', borderRadius: 8,
                      background: 'rgba(255,255,255,0.15)', fontSize: 12
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
                      </svg>
                      <span>{att.filename}</span>
                      {att.size && <span style={{ opacity: 0.6 }}>{att.size} KB</span>}
                    </div>
                  ))}
                </>)}
                {msg.role === 'assistant' && msg.streaming && msg.content && (
                  <span className="streaming-cursor" />
                )}
                {msg.role === 'assistant' && !msg.streaming && extractDocUrls(msg.content).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {extractDocUrls(msg.content).map(doc => (
                      <DocDownloadLink key={doc.url} doc={doc} />
                    ))}
                  </div>
                )}
                {msg.metadata?.emailCards?.length > 0 && (
                  <div className="chat-email-cards">
                    {msg.metadata.emailCards.map(email => {
                      const dateStr = email.date ? new Date(email.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''
                      return (
                        <div
                          key={email.id}
                          className="chat-email-card"
                          onClick={() => {
                            const ids = msg.metadata.emailCards.map(e => e.id)
                            sessionStorage.setItem('email_deeplink_ids', JSON.stringify(ids))
                            window.dispatchEvent(new CustomEvent('email-deeplink', { detail: ids }))
                            onNavigate?.('emails')
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="chat-email-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <path d="M22 7l-10 7L2 7" />
                            </svg>
                          </div>
                          <div className="chat-email-info">
                            <span className="chat-email-sender">{email.sender || email.sender_email}</span>
                            <span className="chat-email-subject">{email.subject}</span>
                          </div>
                          <div className="chat-email-date">{dateStr}</div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {msg.metadata?.moduleCards?.length > 0 && (
                  <ModuleCards cards={msg.metadata.moduleCards} />
                )}
                {msg.role === 'assistant' && !msg.streaming && msg.provider && (
                  <div className="chat-bubble-provider">
                    via {msg.provider === 'claude' ? 'Claude' : msg.provider === 'openai' ? 'GPT' : msg.provider === 'xai' ? 'Grok' : msg.provider === 'ollama' ? 'Ollama' : msg.provider}
                    {msg.model && <span> ({msg.model.split('-').slice(0, 2).join('-')})</span>}
                  </div>
                )}
                {msg.role === 'assistant' && !msg.streaming && !msg.provider && msg.content && (
                  <div className="chat-bubble-provider">via Claude Code</div>
                )}
              </div>
            ))}
            {sending && !messages[messages.length - 1]?.streaming && (
              <div className="chat-bubble chat-bubble-assistant">
                <div className="spinner spinner-sm" />
              </div>
            )}
            {!sending && activeRunning && (
              <div className="chat-bubble chat-bubble-assistant" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
                <div className="spinner spinner-sm" /> Urs arbeitet an diesem Faden…
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div
            className="agent-input-resize-handle"
            onMouseDown={e => {
              e.preventDefault()
              inputResizeStart.current = { y: e.clientY, height: inputHeight }
              setIsInputResizing(true)
            }}
          >
            <div className="agent-input-resize-bar" />
          </div>
          {activeContext && (
            <div
              title={`Bezug: ${activeContext.subject ? activeContext.label + ' — ' + activeContext.subject : activeContext.label}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                maxWidth: 'calc(100% - 76px)', margin: '0 0 8px 60px', padding: '4px 8px 4px 10px',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 999, fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.3
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #FF5500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ opacity: 0.55 }}>Bezug:</span> {activeContext.label}
              </span>
              <button
                type="button"
                onClick={() => setContextDismissed(true)}
                title="Bezug entfernen"
                aria-label="Bezug entfernen"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginLeft: 2, padding: 1, border: 'none', background: 'none',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.5)', borderRadius: 4
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          )}
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 12px 8px' }}>
              {pendingFiles.map((f, i) => (
                <div key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '4px 6px 4px 8px', borderRadius: 8,
                  background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)',
                  fontSize: 12, color: 'rgba(255,255,255,0.9)', maxWidth: '100%'
                }}>
                  {f.kind === 'image' ? (
                    <img src={`data:${f.img.media_type};base64,${f.img.data}`} alt={f.filename}
                      style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
                    </svg>
                  )}
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{f.filename}</span>
                  <button type="button" onClick={() => removePendingFile(i)} title="Entfernen" aria-label="Entfernen"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 1, border: 'none', background: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              ))}
              {uploading && <div className="spinner spinner-sm" style={{ alignSelf: 'center' }} />}
            </div>
          )}
          {polishing && !transcribing && (
            /* Whisper-Nachkorrektur läuft — sichtbarer Hinweis, der Platzhalter ist bei gefülltem Feld unsichtbar */
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
              <div className="spinner spinner-sm" />
              <span>{pendingSendUi ? 'Whisper korrigiert nach — Nachricht wird gleich gesendet...' : 'Whisper korrigiert nach...'}</span>
            </div>
          )}
          <form className="agent-widget-input" onSubmit={handleSubmit}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.txt,.csv,.md,.json,.png,.jpg,.jpeg,.gif,.webp,.heic"
              style={{ display: 'none' }}
              onChange={e => { handleFilesUpload(e.target.files); e.target.value = '' }}
            />
            <button
              type="button"
              className="agent-widget-voice-btn"
              onClick={(e) => { e.preventDefault(); fileInputRef.current?.click() }}
              disabled={sending || uploading}
              title="Datei oder Bild anhängen"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <button
              type="button"
              className={`agent-widget-voice-btn ${recording ? 'recording' : ''} ${transcribing ? 'transcribing' : ''}`}
              onClick={(e) => { e.preventDefault(); recording ? stopVoice() : startVoice() }}
              disabled={transcribing || sending}
              title={recording ? 'Diktat stoppen' : 'Tippen zum Diktieren'}
            >
              {transcribing ? (
                <div className="spinner spinner-sm" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>
            <textarea
              ref={inputRef}
              className="input"
              placeholder={recording ? (dictationRef.current ? 'Sprich jetzt – Text erscheint live...' : 'Aufnahme läuft...') : transcribing ? 'Wird transkribiert...' : polishing ? (pendingSendUi ? 'Whisper korrigiert nach — wird gleich gesendet...' : 'Whisper korrigiert nach...') : 'Nachricht...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={sending || (recording && !dictationRef.current)}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              onDrop={e => { e.preventDefault(); e.stopPropagation(); setFileDragging(false); handleFilesUpload(e.dataTransfer?.files) /* Safari Finder: block path text insertion */ }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.altKey) {
                  e.preventDefault()
                  const ta = e.target
                  const start = ta.selectionStart
                  const end = ta.selectionEnd
                  setInput(input.substring(0, start) + '\n' + input.substring(end))
                  setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + 1 }, 0)
                } else if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  if ((input.trim() || pendingFiles.length > 0) && !sending && !uploading) {
                    handleSubmit(e)
                  }
                }
              }}
              onPaste={e => {
                // Alle Dateien aus der Zwischenablage annehmen, nicht nur Bilder — sonst
                // laesst sich ein im Finder kopiertes PDF nicht einfuegen. Nicht
                // unterstuetzte Typen faengt uploadOneFile mit einer Meldung ab.
                const files = Array.from(e.clipboardData?.files || [])
                if (files.length > 0) { e.preventDefault(); handleFilesUpload(files) }
              }}
              style={{ resize: 'none', height: inputHeight }}
            />
            {sending ? (
              <button type="button" onClick={stopStreaming} title="Abbrechen" style={{
                alignSelf: 'flex-end', width: 36, height: 36, minWidth: 36, borderRadius: '50%',
                background: '#ef4444', border: 'none', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </button>
            ) : (
              <button type="submit" disabled={(!input.trim() && pendingFiles.length === 0) || recording || uploading} style={{
                alignSelf: 'flex-end', width: 36, height: 36, minWidth: 36, borderRadius: '50%',
                background: '#fafafa', border: 'none', color: '#18181b',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: ((!input.trim() && pendingFiles.length === 0) || recording || uploading) ? 0.4 : 1,
                cursor: ((!input.trim() && pendingFiles.length === 0) || recording || uploading) ? 'default' : 'pointer', flexShrink: 0
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            )}
          </form>
        </div>
      )}

      <button className={`agent-widget-fab ${hasNewReply ? 'agent-widget-fab-pulse' : ''}`} onClick={() => {
        // Mobile: navigate to chat page instead of opening panel
        if (window.innerWidth <= 768) {
          onNavigate('chat')
        } else {
          toggleOpen()
        }
      }} title="Agent Chat (⌘K)">
        <svg width="30" height="30" viewBox="1 1.75 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="11.1" y="3" width="1.8" height="5.6" rx="0.9" fill="currentColor" stroke="none" />
          <rect x="3" y="12" width="18" height="9.5" rx="3" />
          <circle cx="8.6" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="15.4" cy="16.8" r="1.4" fill="currentColor" stroke="none" />
        </svg>
        {pendingActions > 0 && (
          <span className="agent-widget-badge">{pendingActions > 9 ? '9+' : pendingActions}</span>
        )}
      </button>
    </>
  )
}
